import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import moment from 'moment';
import classNames from 'classnames';

import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import DatePicker from '@/shared/components/input/DatePicker';
import DayMonthPicker from '@/shared/components/input/DayMonthPicker';
import CurrencySymbol from '@/shared/components/CurrencySymbol';
import { isBeforeToday, validateEndDate } from '@/shared/utils/validationHelpers';
import config from '../../../../config';
import CompanySelector from '@/shared/components/input/CompanySelector';
import PriceGridSelector from '@/shared/components/input/PriceGridSelector';
import HasMultiCompany from '@/shared/components/security/HasMultiCompany';

const messages = defineMessages({
  AssignmentTemplate: 'Assignment template',
  AddAssignment: 'Add assignment',
  TaskTemplate: 'Task template',
  Schedule: 'Schedule',
  Deadline: 'Deadline',
  StartPlanning: 'Start planning',
  EndPlanning: 'End planning',
  Choose: 'Choose',
  NoSchedule: 'No schedule',
  NoAssignmentTemplates: 'There are no assignment templates',
  RelationTypeMismatchWarning:
    'There is a posibiltiy that some relations will be skipped because their type does not match the types defined in the assignement template',
  InvoiceSchedule: 'Invoice schedule',
  Amount: 'Amount',
  LedgerAccount: 'Ledger account',
  OwnCompany: 'Own company',
  PriceGrid: 'Price grid',
  UseRelationSetting: 'Use relation setting',
  AssignmentName: 'Assignment name',
  UniqueAssignmentNameInfo:
    'Multiple assignments per relation allow, provide unique assignment name',
});

const validateScheduleLine = line => {
  if (line.removed) return true; // Removed lines are not validated
  if (!line.planningStart) return false;
  if (!moment(line.planningStart).isValid()) return false;
  if (line.planningStop && !moment(line.planningStop).isValid()) return false;
  if (!line.periodicity || !line.periodicity.id) return false;
  if (line.periodicity.showScheduleDate && !line.scheduleDate) return false;

  return true;
};

class AddAssignmentModal extends Component {
  state = {
    templates: [],
    assignmentTemplate: null,
    assignmentName: null,
    companyId: null,
    priceGridId: null,
    taskConfig: [],
    taskSchedules: [],
    taskTemplates: [],
    datePickerFocus: {},
    invoiceSchedule: {
      schedule: null,
      startDate: moment(),
      endDate: null,
      amount: 0,
      ledgerAccount: '',
      overrideExistingSchedule: false,
    },
    invoiceSchedules: [],
  };

  componentDidMount() {
    this.retrieveAssignmentTemplates();
  }

  retrieveAssignmentTemplates = () => {
    const { intl, relationType } = this.props;

    const assignmentTemplateRequest = api.base.get(
      `${config.appUrls.api}/api/AssignmentTemplate/get`,
      {
        params: {
          relationType,
        },
      },
    );
    const taskConfigRequest = api.base.get(`${config.appUrls.tasks}/api/AssignmentTemplate`);
    const taskTemplatesRequest = api.base.get(
      `${config.appUrls.tasks}/api/TaskTemplates/Schedules`,
      {
        params: {
          relationType: relationType,
        },
      },
    );
    const invoiceScheduleRequest = api.base.get(`${config.appUrls.api}/api/invoiceschedule/get`);

    Promise.all([
      assignmentTemplateRequest,
      taskConfigRequest,
      taskTemplatesRequest,
      invoiceScheduleRequest,
    ]).then(
      ([
        { data: assignmentTemplates },
        { data: taskConfig },
        { data: taskTemplates },
        { data: invoiceSchedules },
      ]) => {
        this.setState({
          taskConfig,
          templates: (assignmentTemplates || [])
            .map(t => ({
              ...t,
              label: intl.formatMessage(listItemMsg('assignmenttemplate', t.id, t.name)),
            }))
            .sort(dynamicSort('label')),
          taskTemplates: taskTemplates.map(x => ({
            ...x,
            label: intl.formatMessage(listItemMsg('tasktemplate', x.id, x.name)),
            periodicities: (x.periodicities || [])
              .map(p => ({
                ...p,
                label: intl.formatMessage(listItemMsg('taskperiodicity', p.id, p.name)),
              }))
              .sort(dynamicSort('label')),
          })),
          invoiceSchedules: (invoiceSchedules || [])
            .map(t => ({
              ...t,
              label: `${intl.formatMessage(
                listItemMsg('invoiceSchedule', t.id, t.name),
              )}          (${intl.formatMessage(listItemMsg('InvoicingType', t.invoicingType))})`,
            }))
            .sort(dynamicSort('label')),
        });
      },
    );
  };

  templateChanged = event => {
    const { templates, taskConfig, taskTemplates } = this.state;
    const { intl } = this.props;
    const templateId = event.target.value;
    const template = templates.find(x => x.id === templateId);
    const assignmentName = template?.allowMultiple
      ? intl.formatMessage(listItemMsg('assignmentTemplate', template.id, template.name))
      : null;
    let taskSchedules = [];
    if (template) {
      taskSchedules = taskConfig
        .reduce((result, item) => {
          if (item.assignmentTemplateId !== template.id) return result;
          const taskTemplate = taskTemplates.find(x => x.id === item.taskTemplateId);
          if (!taskTemplate) return result;
          if (!taskTemplate.periodicities || !taskTemplate.periodicities.length) return result;
          let periodicity = item.taskPeriodicityId
            ? taskTemplate.periodicities.find(x => x.id === item.taskPeriodicityId)
            : null;
          if (!periodicity && taskTemplate.periodicities.length === 1)
            [periodicity] = taskTemplate.periodicities;
          const schedule = {
            type: 1,
            template: {
              id: taskTemplate.id,
              name: taskTemplate.name,
              label: taskTemplate.label,
            },
            periodicities: taskTemplate.periodicities || [],
            periodicity,
            planningStart: moment()
              .utc()
              .startOf('day'),
            planningStop: null,
            scheduleDate: null,
            removed: false,
          };
          result.push(schedule);
          return result;
        }, [])
        .sort(dynamicSort('taskTemplate.label'));
    }
    this.setState({
      assignmentTemplate: template,
      taskSchedules,
      assignmentName,
    });
  };

  updateSchedule = (taskTemplateId, updatedValues) => {
    this.setState(({ taskSchedules }) => ({
      taskSchedules: taskSchedules.map(x => {
        if (x.template.id !== taskTemplateId) return x;
        return { ...x, ...updatedValues };
      }),
    }));
  };

  toggleSchedule = taskTemplateId => {
    this.setState(({ taskSchedules }) => ({
      taskSchedules: taskSchedules.map(x => {
        if (x.template.id !== taskTemplateId) return x;
        return { ...x, removed: !x.removed };
      }),
    }));
  };

  schedulePeriodicityChanged = (taskTemplateId, periodicityId) => {
    this.setState(({ taskSchedules }) => ({
      taskSchedules: taskSchedules.map(x => {
        if (x.template.id !== taskTemplateId) return x;

        const periodicity = periodicityId
          ? x.periodicities.find(p => p.id === periodicityId)
          : null;
        let { scheduleDate } = x;
        if (!periodicity || !periodicity.showScheduleDate) {
          scheduleDate = null;
        }

        return { ...x, periodicity, scheduleDate };
      }),
    }));
  };

  updateInvoiceSchedule = updatedProps => {
    this.setState(({ invoiceSchedule }) => ({
      invoiceSchedule: { ...invoiceSchedule, ...updatedProps },
    }));
  };
  invoiceScheduleChanged = e => {
    const { invoiceSchedules } = this.state;
    const id = e.target.value;
    const schedule = id ? invoiceSchedules.find(x => x.id === id) : null;
    this.updateInvoiceSchedule({ schedule });
  };

  setDatePickerFocus = (picker, focused) => {
    this.setState(({ datePickerFocus }) => ({
      datePickerFocus: {
        ...datePickerFocus,
        [picker]: focused,
      },
    }));
  };
  setAssignmentName = e => {
    this.setState({ assignmentName: e.target.value || null });
  };

  validate = () => {
    const { assignmentTemplate, taskSchedules, invoiceSchedule, assignmentName } = this.state;
    if (!assignmentTemplate || !assignmentTemplate.id) return false;
    if (
      taskSchedules &&
      taskSchedules.length > 0 &&
      taskSchedules.some(x => !validateScheduleLine(x))
    )
      return false;
    if (invoiceSchedule && invoiceSchedule.schedule && invoiceSchedule.schedule.id) {
      if (!invoiceSchedule.startDate) return false;
      if (isBeforeToday(invoiceSchedule.startDate)) return false;
      if (!validateEndDate(invoiceSchedule.endDate, invoiceSchedule.startDate)) return false;
      if (invoiceSchedule.schedule.invoicingType !== 0 && invoiceSchedule.amount < 1) return false;
    }
    if (assignmentTemplate.allowMultiple && !assignmentName?.trim()) return false;

    return true;
  };

  onSave = () => {
    const { onSave } = this.props;
    const {
      assignmentTemplate,
      taskSchedules,
      invoiceSchedule = {},
      companyId,
      priceGridId,
      assignmentName,
    } = this.state;
    if (!this.validate()) return Promise.reject();
    const { schedule, ...invoiceScheduleData } = invoiceSchedule;

    const parameters = {
      assignmentTemplateId: assignmentTemplate.id,
      assignmentName: assignmentTemplate.allowMultiple ? assignmentName?.trim() : null,
      companyId,
      priceGridId,
      taskSchedules: (taskSchedules || [])
        .filter(x => !x.removed)
        .map(x => ({
          type: x.type,
          taskTemplateId: x.template.id,
          periodicityId: x.periodicity.id,
          planningStart: x.planningStart,
          planningStop: x.planningStop,
          scheduleDate: x.scheduleDate,
        })),
      invoiceSchedule:
        schedule && schedule.id
          ? {
              ...invoiceScheduleData,
              scheduleId: schedule.id,
            }
          : null,
    };
    return onSave(parameters);
  };

  render() {
    const {
      templates,
      assignmentTemplate,
      taskSchedules,
      showValidation,
      datePickerFocus,
      invoiceSchedule,
      invoiceSchedules,
      companyId,
      priceGridId,
      assignmentName,
    } = this.state;
    const { intl, onClose, relationType } = this.props;

    const showScheduleDate = taskSchedules.some(
      x => x.periodicity && x.periodicity.showScheduleDate,
    );
    const appliesToCount = assignmentTemplate
      ? (assignmentTemplate.appliesToPerson ? 1 : 0) +
        (assignmentTemplate.appliesToSoleProprietor ? 1 : 0) +
        (assignmentTemplate.appliesToCompany ? 1 : 0)
      : 0;
    const showRelationTypeMismatchWarning =
      relationType === null && !!assignmentTemplate && appliesToCount !== 3;
    const hasInvoiceSchedule =
      invoiceSchedule && invoiceSchedule.schedule && invoiceSchedule.schedule.id;
    const showInvoiceScheduleAmount =
      hasInvoiceSchedule && invoiceSchedule.schedule.invoicingType !== 0;
    const showAssingmentName = assignmentTemplate?.allowMultiple ?? false;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.AddAssignment)}
        show
        onClose={onClose}
        disabled={showValidation && !this.validate()}
        onSave={this.onSave}
        onHoverSave={() => this.setState({ showValidation: true })}
        size="xl"
      >
        <div className="form-horizontal">
          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.AssignmentTemplate} />
            </label>
            <div className="col-sm-5">
              {templates && templates.length > 0 && (
                <select
                  onChange={this.templateChanged}
                  className="form-control"
                  value={(assignmentTemplate && assignmentTemplate.id) || ''}
                >
                  {!assignmentTemplate || !assignmentTemplate.id ? (
                    <option>{intl.formatMessage(messages.Choose)}</option>
                  ) : null}
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              )}
              {templates && templates.length === 0 && (
                <div className="alert alert-error">
                  <FormattedMessage {...messages.NoAssignmentTemplates} />
                </div>
              )}
              {showRelationTypeMismatchWarning && (
                <div className="alert alert-warning margin10T">
                  <FormattedMessage {...messages.RelationTypeMismatchWarning} />
                </div>
              )}
            </div>
          </div>
          {showAssingmentName && (
            <div
              className={classNames('form-group', {
                'has-error': showValidation && !assignmentName?.trim(),
              })}
            >
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.AssignmentName} />
                <i
                  className="fa fa-info-circle margin5L"
                  title={intl.formatMessage(messages.UniqueAssignmentNameInfo)}
                />
              </label>
              <div className="col-sm-5">
                <input
                  type="text"
                  className="form-control"
                  value={assignmentName || ''}
                  maxLength={100}
                  onChange={this.setAssignmentName}
                />
              </div>
            </div>
          )}

          <HasMultiCompany>
            <div className="form-group">
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.OwnCompany} />
              </label>
              <div className="col-sm-5">
                <CompanySelector
                  onChange={value => this.setState({ companyId: value || null })}
                  value={companyId}
                  placeholder={intl.formatMessage(messages.UseRelationSetting)}
                />
              </div>
            </div>
          </HasMultiCompany>
          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.PriceGrid} />
            </label>
            <div className="col-sm-5">
              <PriceGridSelector
                onChange={value => this.setState({ priceGridId: value || null })}
                value={priceGridId}
                placeholder={intl.formatMessage(messages.UseRelationSetting)}
              />
            </div>
          </div>
          {invoiceSchedules && invoiceSchedules.length > 0 ? (
            <div className="form-group">
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.InvoiceSchedule} />
              </label>
              <div className="col-sm-5">
                <select
                  onChange={this.invoiceScheduleChanged}
                  className="form-control"
                  value={
                    (invoiceSchedule && invoiceSchedule.schedule && invoiceSchedule.schedule.id) ||
                    ''
                  }
                >
                  <option>{intl.formatMessage(messages.NoSchedule)}</option>
                  {invoiceSchedules.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {hasInvoiceSchedule ? (
            <Fragment>
              <div
                className={classNames('form-group', {
                  'has-error': showValidation && !invoiceSchedule.startDate,
                })}
              >
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.StartPlanning)}
                </label>
                <div className="col-sm-5">
                  <DatePicker
                    placeholder={intl.formatMessage(messages.StartPlanning)}
                    date={invoiceSchedule.startDate}
                    onDateChange={date =>
                      this.updateInvoiceSchedule({ startDate: date, endDate: undefined })
                    }
                    focused={datePickerFocus.invoicePlanningStart || false}
                    onFocusChange={({ focused }) =>
                      this.setDatePickerFocus('invoicePlanningStart', focused)
                    }
                    id="invoicePlanningStart"
                    required
                    block
                    small
                    isOutsideRange={isBeforeToday}
                  />
                </div>
              </div>
              <div
                className={classNames('form-group', {
                  'has-error':
                    showValidation &&
                    !validateEndDate(invoiceSchedule.endDate, invoiceSchedule.startDate),
                })}
              >
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.EndPlanning)}
                </label>
                <div className="col-sm-5">
                  <DatePicker
                    placeholder={intl.formatMessage(messages.EndPlanning)}
                    date={invoiceSchedule.endDate}
                    block
                    small
                    onDateChange={date => this.updateInvoiceSchedule({ endDate: date })}
                    focused={datePickerFocus.invoicePlanningEnd || false}
                    onFocusChange={({ focused }) =>
                      this.setDatePickerFocus('invoicePlanningEnd', focused)
                    }
                    id="invoicePlanningEnd"
                    isOutsideRange={isBeforeToday}
                  />
                </div>
              </div>
              {showInvoiceScheduleAmount && (
                <div
                  className={classNames('form-group', {
                    'has-error': showValidation && invoiceSchedule.amount < 1,
                  })}
                >
                  <label className="control-label col-sm-4">
                    {intl.formatMessage(messages.Amount)}
                  </label>
                  <div className="col-sm-5">
                    <div className="input-group">
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={invoiceSchedule.amount}
                        onChange={e =>
                          this.updateInvoiceSchedule({ amount: parseFloat(e.target.value) })
                        }
                        className="form-control"
                      />
                      <span className="input-group-addon">
                        <CurrencySymbol />
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.LedgerAccount)}
                </label>
                <div className="col-sm-5">
                  <input
                    type="text"
                    className="form-control"
                    name="ledgeraccount"
                    maxLength="10"
                    value={invoiceSchedule.ledgerAccount}
                    onChange={e => this.updateInvoiceSchedule({ ledgerAccount: e.target.value })}
                    id="ledgeraccount"
                  />
                </div>
              </div>
            </Fragment>
          ) : null}

          {taskSchedules.length > 0 && (
            <div>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...messages.TaskTemplate} />
                    </th>
                    <th>
                      <FormattedMessage {...messages.Schedule} />
                    </th>
                    {showScheduleDate && (
                      <th>
                        <FormattedMessage {...messages.Deadline} />
                      </th>
                    )}
                    <th>
                      <FormattedMessage {...messages.StartPlanning} />
                    </th>
                    <th>
                      <FormattedMessage {...messages.EndPlanning} />
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {taskSchedules.map(taskSchedule => {
                    const taskTemplateId = taskSchedule.template.id;
                    return (
                      <tr ng-repeat="taskSchedule in vm.taskSchedules" key={taskTemplateId}>
                        <td className={classNames({ strike: taskSchedule.removed })}>
                          {taskSchedule.template.label}
                        </td>
                        <td>
                          <select
                            value={taskSchedule.periodicity ? taskSchedule.periodicity.id : ''}
                            id={`periodicity${taskTemplateId}`}
                            name={`periodicity${taskTemplateId}`}
                            onChange={e =>
                              this.schedulePeriodicityChanged(taskTemplateId, e.target.value)
                            }
                            disabled={taskSchedule.removed}
                            required={!taskSchedule.removed}
                            className={classNames('form-control', {
                              markedError:
                                showValidation &&
                                !taskSchedule.removed &&
                                (!taskSchedule.periodicity || !taskSchedule.periodicity.id),
                            })}
                          >
                            {!taskSchedule.periodicity || !taskSchedule.periodicity.id ? (
                              <option>{intl.formatMessage(messages.Choose)}</option>
                            ) : null}
                            {taskSchedule.periodicities.map(periodicity => (
                              <option key={periodicity.id} value={periodicity.id}>
                                {periodicity.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        {showScheduleDate && (
                          <td
                            className={classNames({
                              markedError:
                                showValidation &&
                                !taskSchedule.removed &&
                                taskSchedule.periodicity &&
                                taskSchedule.periodicity.showScheduleDate &&
                                !taskSchedule.scheduleDate,
                            })}
                          >
                            {taskSchedule.periodicity &&
                            taskSchedule.periodicity.showScheduleDate ? (
                              <DayMonthPicker
                                placeholder={intl.formatMessage(messages.Deadline)}
                                id={`scheduleDate${taskTemplateId}`}
                                name={`scheduleDate${taskTemplateId}`}
                                value={taskSchedule.scheduleDate}
                                onChange={date =>
                                  this.updateSchedule(taskTemplateId, { scheduleDate: date })
                                }
                                disabled={taskSchedule.removed}
                                required={!taskSchedule.removed}
                                outputType="moment"
                                block
                                small
                              />
                            ) : null}
                          </td>
                        )}

                        <td
                          className={classNames({
                            markedError:
                              showValidation &&
                              !taskSchedule.removed &&
                              !taskSchedule.planningStart,
                          })}
                        >
                          <DatePicker
                            placeholder={intl.formatMessage(messages.StartPlanning)}
                            date={taskSchedule.planningStart}
                            onDateChange={date =>
                              this.updateSchedule(taskTemplateId, { planningStart: date })
                            }
                            focused={datePickerFocus[`planningStart${taskTemplateId}`]}
                            onFocusChange={({ focused }) =>
                              this.setDatePickerFocus(`planningStart${taskTemplateId}`, focused)
                            }
                            id={`planningStart${taskTemplateId}`}
                            disabled={taskSchedule.removed}
                            required={!taskSchedule.removed}
                            block
                            small
                          />
                        </td>
                        <td>
                          <DatePicker
                            placeholder={intl.formatMessage(messages.EndPlanning)}
                            date={taskSchedule.planningStop}
                            onDateChange={date =>
                              this.updateSchedule(taskTemplateId, { planningStop: date })
                            }
                            focused={datePickerFocus[`planningStop${taskTemplateId}`]}
                            onFocusChange={({ focused }) =>
                              this.setDatePickerFocus(`planningStop${taskTemplateId}`, focused)
                            }
                            id={`planningStop${taskTemplateId}`}
                            disabled={taskSchedule.removed}
                            block
                            small
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-default btn-sm"
                            onClick={() => this.toggleSchedule(taskTemplateId)}
                          >
                            <i
                              className={classNames(
                                'fa',
                                taskSchedule.removed ? 'fa-undo' : 'fa-trash-o',
                              )}
                            />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </OkCancelModal>
    );
  }
}

AddAssignmentModal.propTypes = {
  intl: intlShape.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  relationType: PropTypes.number,
};
AddAssignmentModal.defaultProps = {
  relationType: null,
};

export default AddAssignmentModal;
